import React from "react";
import { useTranslation } from "react-i18next";
import { ByoCostBreakdown } from "../../../../../services/costPrice/costPriceService";
import { CostPriceCategory } from "../../../../../services/costPrice/costPriceCategoriesService";
import { borderPrimary, primaryText, secondaryText, textBlue } from "../../../../../styling/stylingHelper";
import { cn } from "../../../../../lib/utils";
import { eur } from "../../../../orderhistory/utils";
import { BYO_UNASSIGNED_KEY } from "./useCostPriceDetail";

type ByoChoice = ByoCostBreakdown["choices"][number];

export interface ByoChoiceGroup {
    // null category === the "Unassigned" group (rendered last).
    category: CostPriceCategory | null;
    choices: ByoChoice[];
}

interface ByoChoicesSectionProps {
    // Choices grouped by add-on category (assigned groups in order, unassigned last).
    groups: ByoChoiceGroup[];
    onEditChoice: (name: string) => void;
    // Open the add-on category assignment modal for a choice.
    onAssignChoiceCategory: (name: string) => void;
    // Master "Weighted options" toggle. ON = every category weighted (full
    // weighted average); OFF = every category unweighted (base portion only).
    weightedMode: boolean;
    composerSelection: Set<string>;
    onSetWeighted: (on: boolean) => void;
    onToggleChoice: (name: string) => void;
    // Per-category weighting. A group whose key is in `unweightedCategoryKeys`
    // is NOT weighted — it contributes only its picked choices (€0 if none),
    // while the others keep folding in their weighted average.
    unweightedCategoryKeys: Set<string>;
    onSetCategoryWeighted: (groupKey: string, on: boolean) => void;
}

// Small pill-sized weighted toggle reused in each category header and (visually)
// matching the master toggle.
const WeightedSwitch: React.FC<{ on: boolean; onToggle: () => void; label: string }> = ({ on, onToggle, label }) => (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <span className={cn("text-[13px] leading-5 font-medium", secondaryText)}>{label}</span>
        <button
            type="button"
            role="switch"
            aria-checked={on}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className={cn(
                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                on ? "bg-[#0E8965]" : "bg-[#D0D5DD] dark:bg-borderPrimary-dark",
            )}
        >
            <span
                className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white dark:bg-card-dark shadow transition-transform",
                    on ? "translate-x-4" : "translate-x-0.5",
                )}
            />
        </button>
    </label>
);

/**
 * Lists every choice for a BYO dish (window union, merged by exact name),
 * grouped by the choice's add-on category. Each row always shows an on/off
 * select toggle (selecting one turns "Weighted options" off and recomputes the
 * SummaryCards from the selection) plus a "Details" button that opens the
 * choice's cost-price editor (lazy find-or-create).
 *
 * Unassigned choices sit in a trailing group and carry a clickable "Unassigned"
 * badge that opens the add-on category assignment modal. Choices already in a
 * category are shown under that category's header (no per-row label).
 */
const ByoChoicesSection: React.FC<ByoChoicesSectionProps> = ({
    groups, onEditChoice, onAssignChoiceCategory,
    weightedMode, composerSelection, onSetWeighted, onToggleChoice,
    unweightedCategoryKeys, onSetCategoryWeighted,
}) => {
    const { t } = useTranslation();

    const renderRow = (choice: ByoChoice, isUnassigned: boolean, isLast: boolean) => {
        const isOn = composerSelection.has(choice.name);
        const rowBorder = !isLast ? "border-b border-[#E5E7EB] dark:border-borderPrimary-dark" : "";
        return (
            <div
                key={choice.name}
                role="button"
                tabIndex={0}
                onClick={() => onToggleChoice(choice.name)}
                className={cn(
                    "flex items-center justify-between gap-2 px-4 py-3 cursor-pointer",
                    "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors",
                    rowBorder,
                )}
            >
                <div className="flex flex-col min-w-0">
                    <span className={cn("text-[15px] leading-5 font-medium truncate", primaryText)}>
                        {choice.name}
                    </span>
                    <span className={cn("text-[12px] leading-4 mt-0.5", secondaryText)}>
                        {t('costPrice.byo.picksWord', { count: choice.qtySold })}
                    </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {isUnassigned && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onAssignChoiceCategory(choice.name); }}
                            className={cn(
                                "inline-flex items-center rounded-full border border-dashed px-2.5 h-7 text-[12px] leading-4 font-medium",
                                "border-[#D0D5DD] dark:border-borderPrimary-dark text-[#475467] dark:text-textSecondary-dark",
                                "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors",
                            )}
                        >
                            {t('costPrice.byo.unassigned')}
                        </button>
                    )}
                    {choice.isCosted ? (
                        <span className={cn("text-[14px] leading-5 font-medium", textBlue)}>
                            {eur(choice.perPickCost ?? 0)}
                        </span>
                    ) : (
                        <span className="text-[14px] leading-5 font-medium text-[#9CA3AF] dark:text-textTertiary-dark">
                            {t('costPrice.byo.notSet')}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onEditChoice(choice.name); }}
                        className={cn(
                            "inline-flex items-center rounded-lg border px-2.5 h-8 text-[13px] leading-5 font-semibold",
                            "border-[#D0D5DD] dark:border-borderPrimary-dark text-[#344054] dark:text-textSecondary-dark bg-white dark:bg-background-dark",
                            "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors",
                        )}
                    >
                        {t('costPrice.byo.details')}
                    </button>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={isOn}
                        onClick={(e) => { e.stopPropagation(); onToggleChoice(choice.name); }}
                        className={cn(
                            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                            isOn ? "bg-[#0E8965]" : "bg-[#D0D5DD] dark:bg-borderPrimary-dark",
                        )}
                    >
                        <span
                            className={cn(
                                "inline-block h-5 w-5 transform rounded-full bg-white dark:bg-card-dark shadow transition-transform",
                                isOn ? "translate-x-5" : "translate-x-0.5",
                            )}
                        />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <section className="mt-6">
            <div className="flex items-center justify-between mb-3">
                <h3 className={cn("text-[18px] leading-6 font-semibold", primaryText)}>
                    {t('costPrice.byo.choicesTitle')}
                </h3>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <span className={cn("text-[14px] leading-5 font-semibold", primaryText)}>
                        {t('costPrice.byo.weightedOptions')}
                    </span>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={weightedMode}
                        onClick={() => onSetWeighted(!weightedMode)}
                        className={cn(
                            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                            weightedMode ? "bg-[#0E8965]" : "bg-[#D0D5DD] dark:bg-borderPrimary-dark",
                        )}
                    >
                        <span
                            className={cn(
                                "inline-block h-5 w-5 transform rounded-full bg-white dark:bg-card-dark shadow transition-transform",
                                weightedMode ? "translate-x-5" : "translate-x-0.5",
                            )}
                        />
                    </button>
                </label>
            </div>

            <p className={cn("mb-3 text-[13px]", secondaryText)}>
                {weightedMode ? t('costPrice.byo.choicesHint') : t('costPrice.byo.composerHint')}
            </p>

            <div className="flex flex-col gap-4">
                {groups.map((group) => {
                    const groupKey = group.category?.id ?? BYO_UNASSIGNED_KEY;
                    const groupWeighted = !unweightedCategoryKeys.has(groupKey);
                    // The per-category toggle only makes sense when the group has a
                    // visible header. The unassigned group is unlabeled (and thus
                    // toggle-less) when it's the only group — the master toggle covers it.
                    const showHeader = !!group.category || groups.length > 1;
                    return (
                    <div key={groupKey}>
                        {showHeader && (
                            <div className="flex items-center justify-between gap-2 mb-2">
                                {group.category ? (
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span
                                            className="w-2.5 h-2.5 rounded-full shrink-0"
                                            style={{ backgroundColor: group.category.color || '#2563EB' }}
                                        />
                                        <span className={cn("text-[14px] leading-5 font-semibold truncate", primaryText)}>
                                            {group.category.name}
                                        </span>
                                    </div>
                                ) : (
                                    <span className={cn("text-[13px] leading-5 font-medium", secondaryText)}>
                                        {t('costPrice.byo.unassignedGroupTitle')}
                                    </span>
                                )}
                                <WeightedSwitch
                                    on={groupWeighted}
                                    onToggle={() => onSetCategoryWeighted(groupKey, !groupWeighted)}
                                    label={t('costPrice.byo.weightedOptions')}
                                />
                            </div>
                        )}
                        <div className={cn("rounded-xl border overflow-hidden", borderPrimary)}>
                            {group.choices.map((choice, i) =>
                                renderRow(choice, group.category === null, i === group.choices.length - 1),
                            )}
                        </div>
                    </div>
                    );
                })}
            </div>
        </section>
    );
};

export default ByoChoicesSection;
